import React, { useRef } from 'react'
import { Box, Tooltip } from '@mui/material'

interface CellBoundaryHandleProps {
  /** The row's inner width in px, for turning a pixel drag into a percentage. */
  getRowWidth: () => number
  /** Live delta (percentage points moved from the left widget to the right one). */
  onPreview: (deltaPct: number) => void
  /** Final delta, once the pointer is released. */
  onCommit: (deltaPct: number) => void
  /** Double-click: give every widget in the row an equal share again. */
  onDistributeEvenly: () => void
}

const KEYBOARD_STEP_PCT = 2

/**
 * The drag handle on the boundary between two widgets in a row (#1049).
 *
 * Dragging it takes width from one side and gives it to the other; nothing
 * else in the row moves. Double-clicking distributes the row evenly again,
 * which is the exact-thirds case the old width-preset popover existed for.
 *
 * The handle reports a DELTA, not a width: clamping against each widget's
 * minimum is layout math, and lives in `redistributeWidths`.
 */
const CellBoundaryHandle: React.FC<CellBoundaryHandleProps> = ({
  getRowWidth,
  onPreview,
  onCommit,
  onDistributeEvenly,
}) => {
  const drag = useRef<{ startX: number; current: number } | null>(null)

  const toPct = (dx: number): number => {
    const width = getRowWidth()
    if (!width) return 0
    return (dx / width) * 100
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    drag.current = { startX: e.clientX, current: 0 }
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    const delta = toPct(e.clientX - drag.current.startX)
    drag.current.current = delta
    onPreview(delta)
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    e.currentTarget.releasePointerCapture(e.pointerId)
    const final = drag.current.current
    drag.current = null
    onCommit(final)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    onCommit(e.key === 'ArrowRight' ? KEYBOARD_STEP_PCT : -KEYBOARD_STEP_PCT)
  }

  return (
    <Tooltip
      title="Drag to resize. Double-click to even out the row."
      followCursor
      slotProps={{ tooltip: { sx: { fontSize: '0.875rem' } } }}
    >
      <Box
        role="separator"
        aria-label="Resize widgets"
        aria-orientation="vertical"
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={onDistributeEvenly}
        onKeyDown={handleKeyDown}
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: -7,
          width: 14,
          cursor: 'col-resize',
          zIndex: 4,
          touchAction: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Discoverable at rest (#1177): the boundary shows without a hover,
          // and hover/focus brings it to full strength.
          opacity: 0.5,
          transition: 'opacity 0.15s',
          '&:hover, &:focus-visible': { opacity: 1 },
        }}
      >
        {/* Big and bright (#1177): a tall, wide bar with a white ring so it
            stands against whatever widget sits behind the boundary. */}
        <Box
          sx={{
            width: 8,
            height: 'clamp(96px, 60%, 320px)',
            borderRadius: 4,
            backgroundColor: 'info.main',
            boxShadow: '0 0 0 2px rgba(255,255,255,0.9)',
          }}
        />
      </Box>
    </Tooltip>
  )
}

export default CellBoundaryHandle
